import HttpService from "./htttp.service";

class WorkspaceService {
    /**
     * Create a new workspace.
     * @param {string} name - Name of the workspace.
     * @returns {Promise<Object>} - API response.
     */
    async createWorkspace(name, slug = null) {
        try {
            const payload = { name, slug };
            const response = await HttpService.post("/workspace", payload);
            return response;
        } catch (error) {
            console.error("Error creating workspace:", error);
            throw error;
        }
    }

    /**
     * Preview a unique slug for a given name.
     * @param {string} name 
     * @returns {Promise<Object>}
     */
    async previewSlug(name) {
        try {
            const response = await HttpService.post("/workspace/preview-slug", { name });
            return response;
        } catch (error) {
            console.error("Error previewing slug:", error);
            throw error;
        }
    }

    /**
     * Validate if a slug is available.
     * @param {string} slug 
     * @returns {Promise<Object>}
     */
    async validateSlug(slug) {
        try {
            const response = await HttpService.get(`/workspace/validate-slug/${slug}`);
            return response;
        } catch (error) {
            console.error("Error validating slug:", error);
            throw error;
        }
    }

    /**
     * Fetch workspace details by slug.
     * @param {string} slug - Unique workspace slug.
     * @returns {Promise<Object>} - API response.
     */
    async fetchWorkspaceBySlug(slug) {
        try {
            const response = await HttpService.get(`/workspace/${slug}`);
            return response;
        } catch (error) {
            console.error(`Error fetching workspace (slug: ${slug}):`, error);
            throw error;
        }
    }

    // ========== WORKSPACE PROFILES ==========

    async fetchWorkspaceProfiles() {
        try {
            const response = await HttpService.get(`/workspace/workspace-profile`);
            return response;
        } catch (error) {
            console.error("Error fetching workspace profiles:", error);
            throw error;
        }
    }

    async saveWorkspaceProfile(profileId, platform) {
        try {
            const payload = {
                data: {
                    type: "workspaceProfiles",
                    attributes: { profileId, platform },
                },
            };
            const response = await HttpService.post(`/workspace/workspace-profile/save`, payload);
            return response;
        } catch (error) {
            console.error("Error saving workspace profile:", error);
            throw error;
        }
    }

    async deleteWorkspaceProfiles(profileIds, platform) {
        try {
            const payload = {
                data: {
                    type: "workspaceProfiles",
                    attributes: { profileIds, platform },
                },
            };
            const response = await HttpService.delete(`/workspace/workspace-profile/delete`, payload);
            return response;
        } catch (error) {
            console.error("Error deleting workspace profiles:", error);
            throw error;
        }
    }

    // ========== WORKSPACE SUBSCRIPTIONS ==========

    async fetchWorkspaceSubscription() {
        try {
            const response = await HttpService.get(`/workspace/workspace-subscription`);
            return response;
        } catch (error) {
            console.error("Error fetching workspace subscription:", error);
            throw error;
        }
    }

    async saveWorkspaceSubscription(subscriptionInfo) {
        try {
            const response = await HttpService.post(`/workspace/workspace-subscription`, { subscriptionInfo });
            return response;
        } catch (error) {
            console.error("Error saving workspace subscription:", error);
            throw error;
        }
    }

    async updateWorkspaceSubscription(updates) {
        try {
            const response = await HttpService.patch(`/workspace/workspace-subscription`, { updates });
            return response;
        } catch (error) {
            console.error("Error updating workspace subscription:", error);
            throw error;
        }
    }

    async cancelWorkspaceSubscription() {
        try {
            return await HttpService.post(`/workspace/workspace-subscription/cancel`);
        } catch (error) {
            console.error("Error cancelling workspace subscription:", error);
            throw error;
        }
    }

    async reactivateWorkspaceSubscription() {
        try {
            return await HttpService.patch(`/workspace/workspace-subscription/reactivate`);
        } catch (error) {
            console.error("Error reactivating workspace subscription:", error);
            throw error;
        }
    }

    async fetchWorkspaceEntitlements() {
        try {
            return await HttpService.get(`/workspace/entitlements`);
        } catch (error) {
            console.error("Error fetching workspace entitlements:", error);
            throw error;
        }
    }

    async fetchWorkspaceSubscriptionUsage() {
        try {
            const response = await HttpService.get(`/workspace/workspace-subscription-usage`);
            return response;
        } catch (error) {
            console.error("Error fetching workspace subscription usage:", error);
            throw error;
        }
    }

    async updateWorkspaceSubscriptionUsage(updates) {
        try {
            return await HttpService.patch(`/workspace/workspace-subscription-usage`, { updates });
        } catch (error) {
            console.error("Error updating workspace subscription usage:", error);
            throw error;
        }
    }

    // ========== WORKSPACE SNAPSHOTS ==========

    async fetchWorkspaceSnapshots(snapshotId = null) {
        try {
            const endpoint = snapshotId ? `/workspace/workspace-snapshot/${snapshotId}` : `/workspace/workspace-snapshot`;
            return await HttpService.get(endpoint);
        } catch (error) {
            console.error("Error fetching workspace snapshots:", error);
            throw error;
        }
    }

    async saveWorkspaceSnapshot(snapshotInfo) {
        try {
            const response = await HttpService.post(`/workspace/workspace-snapshot`, { snapshotInfo });
            return response;
        } catch (error) {
            console.error("Error saving workspace snapshot:", error);
            throw error;
        }
    }

    async updateWorkspaceSnapshot(snapshotId, snapshotInfo) {
        try {
            const response = await HttpService.patch(`/workspace/workspace-snapshot/${snapshotId}`, { snapshotInfo });
            return response;
        } catch (error) {
            console.error("Error updating workspace snapshot:", error);
            throw error;
        }
    }

    async deleteWorkspaceSnapshot(snapshotId) {
        try {
            const response = await HttpService.delete(`/workspace/workspace-snapshot/${snapshotId}`);
            return response;
        } catch (error) {
            console.error("Error deleting workspace snapshot:", error);
            throw error;
        }
    }

    // ========== WORKSPACE REPORTS ==========

    async fetchWorkspaceReports() {
        try {
            return await HttpService.get("/workspace/workspace-report");
        } catch (error) {
            console.error("Error fetching workspace reports:", error);
            throw error;
        }
    }

    async createWorkspaceReport(reportData) {
        try {
            const response = await HttpService.post("/workspace/workspace-report", reportData);
            return response;
        } catch (error) {
            console.error("Error creating workspace report:", error);
            throw error;
        }
    }

    async updateWorkspaceReport(reportId, updateData) {
        try {
            const response = await HttpService.patch(`/workspace/workspace-report/${reportId}`, updateData);
            return response;
        } catch (error) {
            console.error("Error updating workspace report:", error);
            throw error;
        }
    }

    async deleteWorkspaceReport(reportId) {
        try {
            const response = await HttpService.delete(`/workspace/workspace-report/${reportId}`);
            return response;
        } catch (error) {
            console.error("Error deleting workspace report:", error);
            throw error;
        }
    }

    // ========== OTHER WORKSPACE CONTEXT ==========

    async fetchWorkspaceAddons() {
        try {
            return await HttpService.get("/workspace/workspace-addons");
        } catch (error) {
            console.error("Error fetching workspace add-ons:", error);
            throw error;
        }
    }

    async fetchWorkspaceBrandingPacks() {
        try {
            return await HttpService.get("/workspace/workspace-branding");
        } catch (error) {
            console.error("Error fetching workspace branding packs:", error);
            throw error;
        }
    }

    async createWorkspaceBrandingPack(formData) {
        try {
            const response = await HttpService.post("/workspace/workspace-branding", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (error) {
            console.error("Error creating workspace branding pack:", error);
            throw error;
        }
    }

    async updateWorkspaceBrandingPack(packId, formData) {
        try {
            const response = await HttpService.put(`/workspace/workspace-branding/${packId}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (error) {
            console.error("Error updating workspace branding pack:", error);
            throw error;
        }
    }

    async deleteWorkspaceBrandingPack(packId) {
        try {
            const response = await HttpService.delete(`/workspace/workspace-branding/${packId}`);
            return response;
        } catch (error) {
            console.error("Error deleting workspace branding pack:", error);
            throw error;
        }
    }

    async setDefaultWorkspaceBrandingPack(packId) {
        try {
            const response = await HttpService.patch(`/workspace/workspace-branding/${packId}/default`);
            return response;
        } catch (error) {
            console.error("Error setting default workspace branding pack:", error);
            throw error;
        }
    }

    async switchActiveWorkspace(workspaceId) {
        try {
            const response = await HttpService.post("/workspace/workspace-switch", { workspaceId });
            return response;
        } catch (error) {
            console.error("Error switching workspace:", error);
            throw error;
        }
    }

    async checkWorkspaceStatus() {
        try {
            return await HttpService.get("/workspace/workspace-status");
        } catch (error) {
            console.error("Error checking workspace status:", error);
            throw error;
        }
    }

    async sendInvitation(workspaceId, email, role) {
        try {
            const payload = { workspaceId, email, role };
            const response = await HttpService.post("/workspace/invite", payload);
            return response;
        } catch (error) {
            console.error("Error sending invitation:", error);
            throw error;
        }
    }

    async fetchInvitations(workspaceId) {
        try {
            const response = await HttpService.get(`/workspace/invitations/${workspaceId}`);
            return response;
        } catch (error) {
            console.error("Error fetching invitations:", error);
            throw error;
        }
    }

    async describeInvitation(token) {
        try {
            const response = await HttpService.get(`/workspace/invitations/describe/${token}`);
            return response;
        } catch (error) {
            console.error("Error describing invitation:", error);
            throw error;
        }
    }

    async deleteInvitation(workspaceId, invitationId) {
        try {
            const response = await HttpService.post(`/workspace/invitations/remove`, { workspaceId, invitationId });
            return response;
        } catch (error) {
            console.error("Error deleting invitation:", error);
            throw error;
        }
    }

    async fetchWorkspaceMembers(workspaceId) {
        try {
            const response = await HttpService.get(`/workspace/members/${workspaceId}`);
            return response;
        } catch (error) {
            console.error("Error fetching workspace members:", error);
            throw error;
        }
    }

    async updateMemberRole(workspaceId, userId, role) {
        try {
            const response = await HttpService.patch(`/workspace/members/role`, { workspaceId, userId, role });
            return response;
        } catch (error) {
            console.error("Error updating member role:", error);
            throw error;
        }
    }

    async removeMember(workspaceId, userId) {
        try {
            const response = await HttpService.post(`/workspace/members/remove`, { workspaceId, userId });
            return response;
        } catch (error) {
            console.error("Error removing member:", error);
            throw error;
        }
    }

    // ========== WORKSPACE MANAGEMENT (OWNER ONLY) ==========

    async renameWorkspace(workspaceId, name) {
        try {
            const response = await HttpService.patch(`/workspace/${workspaceId}`, { name });
            return response;
        } catch (error) {
            console.error("Error renaming workspace:", error);
            throw error;
        }
    }

    async deleteWorkspace(workspaceId) {
        try {
            const response = await HttpService.delete(`/workspace/${workspaceId}`);
            return response;
        } catch (error) {
            console.error("Error deleting workspace:", error);
            throw error;
        }
    }

    async transferOwnership(workspaceId, targetUserId) {
        try {
            const response = await HttpService.patch(`/workspace/${workspaceId}/transfer-owner`, { targetUserId });
            return response;
        } catch (error) {
            console.error("Error transferring workspace ownership:", error);
            throw error;
        }
    }
}

export default new WorkspaceService();

